/**
 * 점유 테이블 QR 안내 문구 (#674).
 *
 * 점유된 테이블의 QR 스캔(또는 세션 시작 시도) 시 BE 가
 * `{ message, data: { active_session } }` (409) 를 반환한다. 서버 message 는
 * ko 고정이므로 FE 가 active_session 데이터 기반으로 자체 i18n 문구를 표시한다.
 *
 * 15개 지원 locale 전수 정의 — i18n drift 는 qrOccupied.test.ts 가 회귀 게이트
 * (#650 paymentResult3ds 패턴).
 *
 * @see src/pages/QRVerifyPage.tsx
 * @see src/lib/api/qr.ts extractOccupiedTableSession
 */
import type { Locale } from '@/config/locales';

export interface QrOccupiedTranslation {
  /** 점유 화면 제목 — '이 테이블은 이용 중입니다' */
  title: string;
  /** 안내 문구 — 새 주문 불가 + 직원 문의 유도 */
  description: string;
  /** 이용 시작 시각 라벨 */
  startedAtLabel: string;
  /** 경과 시간 라벨 */
  elapsedLabel: string;
  /** 경과 시간 포맷 — hours 0 이면 분만 표시 */
  elapsedTime: (hours: number, minutes: number) => string;
}

export const qrOccupiedTranslations: Record<Locale, QrOccupiedTranslation> = {
  es: {
    title: 'Esta mesa está ocupada',
    description:
      'Ya hay una sesión activa en esta mesa, por lo que no se puede iniciar un nuevo pedido. Por favor consulta al personal.',
    startedAtLabel: 'Inicio de uso',
    elapsedLabel: 'Tiempo transcurrido',
    elapsedTime: (hours, minutes) => (hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`),
  },
  en: {
    title: 'This table is currently in use',
    description:
      'There is already an active session at this table, so a new order cannot be started. Please ask a staff member for help.',
    startedAtLabel: 'Started at',
    elapsedLabel: 'Time elapsed',
    elapsedTime: (hours, minutes) => (hours > 0 ? `${hours} hr ${minutes} min` : `${minutes} min`),
  },
  ko: {
    title: '이 테이블은 이용 중입니다',
    description: '이미 이용 중인 세션이 있어 새 주문을 시작할 수 없습니다. 직원에게 문의해 주세요.',
    startedAtLabel: '이용 시작',
    elapsedLabel: '경과 시간',
    elapsedTime: (hours, minutes) => (hours > 0 ? `${hours}시간 ${minutes}분` : `${minutes}분`),
  },
  pt: {
    title: 'Esta mesa está em uso',
    description:
      'Já existe uma sessão ativa nesta mesa, portanto não é possível iniciar um novo pedido. Por favor, fale com um funcionário.',
    startedAtLabel: 'Início do uso',
    elapsedLabel: 'Tempo decorrido',
    elapsedTime: (hours, minutes) => (hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`),
  },
  fr: {
    title: 'Cette table est occupée',
    description:
      'Une session est déjà active à cette table, il est donc impossible de commencer une nouvelle commande. Veuillez vous adresser au personnel.',
    startedAtLabel: 'Début d’utilisation',
    elapsedLabel: 'Temps écoulé',
    elapsedTime: (hours, minutes) => (hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`),
  },
  zh: {
    title: '该餐桌正在使用中',
    description: '该餐桌已有进行中的用餐会话，无法开始新的点餐。请咨询店员。',
    startedAtLabel: '开始时间',
    elapsedLabel: '已用时间',
    elapsedTime: (hours, minutes) => (hours > 0 ? `${hours}小时${minutes}分钟` : `${minutes}分钟`),
  },
  ja: {
    title: 'このテーブルは利用中です',
    description:
      'このテーブルには既に利用中のセッションがあるため、新しい注文を開始できません。スタッフにお問い合わせください。',
    startedAtLabel: '利用開始',
    elapsedLabel: '経過時間',
    elapsedTime: (hours, minutes) => (hours > 0 ? `${hours}時間${minutes}分` : `${minutes}分`),
  },
  hi: {
    title: 'यह टेबल अभी उपयोग में है',
    description:
      'इस टेबल पर पहले से एक सक्रिय सत्र चल रहा है, इसलिए नया ऑर्डर शुरू नहीं किया जा सकता। कृपया स्टाफ से संपर्क करें।',
    startedAtLabel: 'उपयोग प्रारंभ',
    elapsedLabel: 'बीता समय',
    elapsedTime: (hours, minutes) =>
      hours > 0 ? `${hours} घंटे ${minutes} मिनट` : `${minutes} मिनट`,
  },
  de: {
    title: 'Dieser Tisch ist derzeit besetzt',
    description:
      'An diesem Tisch läuft bereits eine aktive Sitzung, daher kann keine neue Bestellung gestartet werden. Bitte wenden Sie sich an das Personal.',
    startedAtLabel: 'Beginn',
    elapsedLabel: 'Verstrichene Zeit',
    elapsedTime: (hours, minutes) =>
      hours > 0 ? `${hours} Std. ${minutes} Min.` : `${minutes} Min.`,
  },
  it: {
    title: 'Questo tavolo è occupato',
    description:
      'A questo tavolo è già attiva una sessione, quindi non è possibile avviare un nuovo ordine. Si prega di rivolgersi al personale.',
    startedAtLabel: 'Inizio utilizzo',
    elapsedLabel: 'Tempo trascorso',
    elapsedTime: (hours, minutes) => (hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`),
  },
  ru: {
    title: 'Этот столик сейчас занят',
    description:
      'За этим столиком уже идёт активная сессия, поэтому новый заказ начать нельзя. Пожалуйста, обратитесь к персоналу.',
    startedAtLabel: 'Начало',
    elapsedLabel: 'Прошло времени',
    elapsedTime: (hours, minutes) => (hours > 0 ? `${hours} ч ${minutes} мин` : `${minutes} мин`),
  },
  ar: {
    title: 'هذه الطاولة قيد الاستخدام حاليًا',
    description:
      'توجد بالفعل جلسة نشطة على هذه الطاولة، لذلك لا يمكن بدء طلب جديد. يُرجى مراجعة أحد الموظفين.',
    startedAtLabel: 'بداية الاستخدام',
    elapsedLabel: 'الوقت المنقضي',
    elapsedTime: (hours, minutes) =>
      hours > 0 ? `${hours} ساعة ${minutes} دقيقة` : `${minutes} دقيقة`,
  },
  vi: {
    title: 'Bàn này hiện đang được sử dụng',
    description:
      'Bàn này đã có phiên đang hoạt động nên không thể bắt đầu đơn hàng mới. Vui lòng liên hệ nhân viên.',
    startedAtLabel: 'Bắt đầu sử dụng',
    elapsedLabel: 'Thời gian đã dùng',
    elapsedTime: (hours, minutes) =>
      hours > 0 ? `${hours} giờ ${minutes} phút` : `${minutes} phút`,
  },
  th: {
    title: 'โต๊ะนี้กำลังถูกใช้งานอยู่',
    description:
      'โต๊ะนี้มีเซสชันที่กำลังใช้งานอยู่แล้ว จึงไม่สามารถเริ่มออเดอร์ใหม่ได้ กรุณาติดต่อพนักงาน',
    startedAtLabel: 'เริ่มใช้งาน',
    elapsedLabel: 'เวลาที่ใช้ไป',
    elapsedTime: (hours, minutes) =>
      hours > 0 ? `${hours} ชม. ${minutes} นาที` : `${minutes} นาที`,
  },
  nl: {
    title: 'Deze tafel is momenteel in gebruik',
    description:
      'Er is al een actieve sessie aan deze tafel, dus er kan geen nieuwe bestelling worden gestart. Vraag het personeel om hulp.',
    startedAtLabel: 'Begintijd',
    elapsedLabel: 'Verstreken tijd',
    elapsedTime: (hours, minutes) => (hours > 0 ? `${hours} u ${minutes} min` : `${minutes} min`),
  },
};

export function getQrOccupiedTranslation(
  locale: Locale | null | undefined,
  fallback: Locale = 'es',
): QrOccupiedTranslation {
  return qrOccupiedTranslations[locale ?? fallback] ?? qrOccupiedTranslations[fallback];
}
